import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { secureGet } from "../../config/api";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import useIsMounted from "../../hooks/useIsMounted";
import SnackbarNotification from "../../components/SnackbarNotification";
import Rows from "../../components/Rows"; 

const MAD = new Intl.NumberFormat("fr-FR");
const PCT = (v) => `${Number(v ?? 0).toFixed(0)}%`;

const ProgressBar = ({ value }) => (
  <div className="w-full">
    <div className="w-full h-4 rounded-full bg-[#6B78F8] bg-opacity-35 overflow-hidden">
      <div
        className="h-4 rounded-full bg-[#6B78F8] bg-opacity-68"
        style={{ width: `${Math.min(100, Math.max(0, Number(value || 0)))}%` }}
      />
    </div>
  </div>
);

const KpiCard = ({ accent = "bg-secondary", color, title, value, subtitle, unit }) => (
  <div
    className="bg-white rounded-[12px] relative flex flex-col justify-center min-h-[110px] px-6 py-4"
    style={{ boxShadow: "0 4px 10px rgba(0,0,0,0.1)" }}
  >
    <div className={`absolute left-0 top-1/2 -translate-y-1/2 h-[60%] w-[6px] rounded-r-md ${accent}`} />
    <h4 className="text-[#000000] text-[20px] font-medium">{title}</h4>
    <div className="flex items-end gap-2 min-w-0">
      <span className="font-bold text-[27px] leading-none mb-2" style={{ color: color }}>{value}</span>
      {unit && (
        <span className="font-bold text-[27px] leading-none mb-2" style={{ color: color }}>{unit}</span>
      )}
    </div>
    {subtitle ? <p className="text-[14px] font-medium text-[#75808E] mt-1">{subtitle}</p> : null}
  </div>
);

const Dashboard = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [dash, setDash] = useState(null);

  const showSnackbar = (message, severity = "success", manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const controllerRef = useRef(new AbortController());

  const fetchDashboards = useCallback(async () => {
    controllerRef.current.abort();
    controllerRef.current = new AbortController();
    const signal = controllerRef.current.signal;

    try {
      const response = await secureGet("/inventories/dashboard", showSnackbar, { signal });
      console.log(response);  
      setDash(response?.data || response);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Failed to fetch dashboards:", error);
      }
    }
  }, [isMounted]);

  useEffect(() => {
    fetchDashboards();
    return () => controllerRef.current.abort();
  }, [fetchDashboards]);

  console.log(dash);

  const globalCompletion = dash?.globalCompletion || 0;
  const globalPrecision = dash?.glocalPrecision || 0;
  const globalEcartQty = dash?.ecartQuantity || 0;
  const globalEcartValue = dash?.ecartValue || 0;

  const details = Array.isArray(dash?.details) ? dash.details : [];

  // Columns for Rows table
  const columns = useMemo(
    () => [
      {
        key: "repoName",
        label: "Inventaire",
        width: "15%",
      },
      {
        key: "completion",
        label: "% Avancement",
        render: (val) => (
          <div className="flex items-center gap-3 min-w-[220px]">
            <div className="flex-1"><ProgressBar value={val} /></div>
            <span className="text-[#043F88] font-bold text-[16px]">{PCT(val)}</span>
          </div>
        ),
        width: "45%",
      },
      {
        key: "ecartQuantity",
        label: "Nombre d’écarts",
        render: (val) => <span className="text-[#38485C] font-bold text-[16px]">{MAD.format(val)}</span>,
        width: "15%",
      },
      // text-[#6B78F8] font-bold text-[16px]
      {
        key: "ecartValue",
        label: "Valeur écarts",
        render: (val) => <span className="text-[#6B78F8] font-bold text-[16px]">{MAD.format(val)} MAD</span>,
        width: "15%",
      },
      {
        key: "precision",
        label: "Précision",
        render: (val) => <span className="font-bold text-[16px] text-[#38485C]">{Number(val ?? 0).toFixed(1)} %</span>,
        width: "10%",
      },
    ],
    [t]
  );

  return (
    <div className="">
      <Helmet>
        <title>GrayFox - {t("titles.dashboard")}</title>
      </Helmet>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-[40px] font-semibold text-[#000000]">
          {t("titles.dashboard")}
        </div>
      </div>

      {/* KPI cards (match your provided style) */}
      <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
        <KpiCard
          accent="bg-[#06D6A0]"
          color="#06D6A0"
          title="% Avancement Global"
          value={Number(globalCompletion || 0).toFixed(0)}
          unit="%"
          subtitle=""
        />
        <KpiCard
          accent="bg-[#6B78F8]"
          color="#6B78F8"
          title="Nombre d’Écarts Global"
          value={MAD.format(globalEcartQty)}
          subtitle=""
        />
        <KpiCard
          accent="bg-[#043F88]"
          color="#043F88"
          title="Valeur Écarts Globale"
          value={MAD.format(globalEcartValue)}
          unit="MAD"
          subtitle=""
        />
        <KpiCard
          accent="bg-[#0283F4]"
          color="#0283F4"
          title="Précision Globale"
          value={Number(globalPrecision || 0).toFixed(1)}
          unit="%"
          subtitle=""
        />
      </div>

      {/* Section title */}
      <div className="mt-8">
        <h3 className="text-[24px] font-semibold text-[#000000]">
          % D’écarts (Global) • Précision Globale Par Dépôt
        </h3>
      </div>

      {/* Table */}
      <div className="mt-4">
        <Rows columns={columns} data={details} />
      </div>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default Dashboard;
